import { useEffect, useState } from "react";
import { useT } from "../../i18n/hooks.ts";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DailySeries } from "../../model/dailySeries.ts";
import { formatScaleDate, type SeriesScale } from "../../model/seriesScale.ts";
import { Spinner } from "../Spinner.tsx";

/**
 * One "Input series" chart (SeriesPanel.tsx) — always exactly one line, the
 * sub-basin/aquifer currently selected on the map or card view (see
 * MapSeriesMode, SubBasinMap.tsx/SubBasinCanvas.tsx). No filter UI here —
 * there used to be a checkbox per entity, dropped: with a real project's
 * worth of sub-basins that was either a wall of checkboxes or a "too many
 * to list" message, and it let the 4 series types show different sets
 * instead of all agreeing on the same one. `selectedCode` — which one to
 * show — is computed by SeriesPanel.tsx from the shared map/card
 * selection, falling back to the first entity with its own column until
 * the user actually clicks one.
 */
export function SeriesChart<T>({
  series,
  entities,
  entityCodeOf,
  nameOf,
  columnIndexOf,
  selectedCode,
  scale,
  loading,
}: {
  series: DailySeries | null;
  entities: T[];
  entityCodeOf: (entity: T) => number;
  nameOf: (entity: T) => string;
  columnIndexOf: (entity: T) => number;
  selectedCode: number | undefined;
  /** The project's own scale — decides how the x-axis's ISO dates are displayed (dd/mm/aaaa or mm/aaaa). */
  scale: SeriesScale;
  /** The series is mid-upload (a new CSV being parsed) — shows a spinner instead of the (still stale, or blank) chart. */
  loading?: boolean;
}) {
  const t = useT();

  if (loading) return <Spinner label={t("seriesPanel.loading")} />;
  if (!series) return <p className="muted-text">{t("seriesPanel.noSeriesYet")}</p>;

  const selected = entities.find((e) => entityCodeOf(e) === selectedCode);
  const columnIndex = selected ? columnIndexOf(selected) : 0;
  if (!selected || columnIndex <= 0 || columnIndex - 1 >= series.columns.length) {
    return <p className="muted-text">{t("seriesPanel.noSeriesYet")}</p>;
  }

  return (
    <div className="chart">
      <p className="muted-text">{t("seriesPanel.showing", { name: nameOf(selected) })}</p>
      <SeriesChartBody
        series={series}
        name={nameOf(selected)}
        columnIndex={columnIndex}
        scale={scale}
      />
    </div>
  );
}

function SeriesChartBody({
  series,
  name,
  columnIndex,
  scale,
}: {
  series: DailySeries;
  name: string;
  columnIndex: number;
  scale: SeriesScale;
}) {
  const t = useT();
  const [data, setData] = useState<{ date: string; value: number }[] | null>(null);

  // Deferred to an effect (not computed directly in render) so the Spinner
  // below is guaranteed at least one real frame on screen first — an
  // effect always runs after the browser paints. A real project's series
  // can be tens of thousands of rows; without this the remap would
  // silently block the very paint that would show the spinner, leaving a
  // frozen/blank chart with no feedback instead.
  useEffect(() => {
    setData(null);
    const id = window.setTimeout(() => {
      setData(series.dates.map((date, i) => ({ date, value: series.values[columnIndex - 1][i] })));
    }, 0);
    return () => window.clearTimeout(id);
  }, [series, columnIndex]);

  if (!data) return <Spinner label={t("seriesPanel.loading")} />;

  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="date" tickFormatter={(iso: string) => formatScaleDate(iso, scale)} />
        <YAxis />
        <Tooltip />
        <Line
          type="monotone"
          dataKey="value"
          name={name}
          stroke="#3fa7b3"
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
